import {Form, Statistic, Input, Modal, Button, TreeSelect, Checkbox, Select, Space, Typography, Table, Divider, FormInstance, SelectProps} from 'antd';
import React, {useState, useEffect, useRef} from 'react';
import { TEVService } from '../../tevapi/io';
import {useNavigate} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useQuery} from '@tanstack/react-query';
import {LoadingAnimation} from '../loading-page';
import {DispositionSelect} from '../dispositions/selection';
import {useDebounce} from 'use-debounce';
import {ICollectionTree, IColumnList} from '../../tevapi/interfaces';
import {DataNode} from 'antd/es/tree';


const transformCollectionTreeReturn = (data: ICollectionTree[]): DataNode[] => {
  return data.map(
    (value) => ({
      title: value.name,
      key: value.pk,
      value: value.pk,
      children: transformCollectionTreeReturn(value.children),
    })
  );
};


export const CollectionSelect: React.FC<{defaultValue?: number | undefined, onChange: (value: number) => void}> = (props) => {
  const [queryStr, setQueryStr] = useState<string>("");
  const [debouncedQuery] = useDebounce(queryStr, 300);

  const collectionQuery = useQuery({
    queryKey: ["collections", debouncedQuery],
    queryFn: async (): Promise<DataNode[]> => {
      const response = await TEVService.get(
        "/collections/", {params: {name: debouncedQuery}}
      );
      return transformCollectionTreeReturn(response.data);
    },
  });
  return (
    <TreeSelect
      treeData={collectionQuery.data}
      showSearch
      onSearch={setQueryStr}
      defaultValue={props.defaultValue}
      loading={collectionQuery.isLoading}
      filterTreeNode={false}
      style={{minWidth: "200px"}}
      onSelect={props.onChange}
    />
  );
};


export const ColumnListSelect: React.FC<{defaultValue?: number, onChange: (value: number) => void}> = (props) => {
  const [queryStr, setQueryStr] = useState<string>("");
  const [debouncedQuery] = useDebounce(queryStr, 300);

  const query = useQuery({
    queryKey: ["columnlists", debouncedQuery],
    queryFn: async(): Promise<SelectProps["options"]> => {
      const response = await TEVService.get<IColumnList[]>(
        "/column-lists/",
        {params: {name: debouncedQuery}}
      );
      return response.data.map(
        (value) => ({
          label: `${value.name} | ${value.item_class}`,
          value: value.pk,
        })
      );
    },
  });

  return (
    <Select
      options={query.data}
      loading={query.isLoading}
      showSearch
      onSearch={setQueryStr}
      defaultValue={props.defaultValue}
      onSelect={props.onChange}
    />
  );
};


const transformReturn = (json) => {
  return json.map(
    row => {
      return {
        title: row.name,
        value: row.pk,
        children: transformReturn(row.children),
      };
    }
  );
};


export const onOkFactory = (form, navigation, url, setModalOpen, method="post") => {
  return () => {
    form.validateFields()
      .then(
        (values) => {
          const func = TEVService[method];
          func(
            url,
            values,
            {validateStatus: (status) => status < 400}
          )
            .then(
              response => {
                if (response.status >= 300) {
                  navigation(response.data.url);
                }
                setModalOpen(false);
              }
            );
        }
      )
  };
};


const transformColumns = (columnLists) => {
  const results = [];
  for (const columns of columnLists) {
    results.push({
      label: columns.name,
      value: columns.pk,
    });
  }
  return results;
};

export const CreateCollectionForm: React.FC<{form: FormInstance, defaultValue?: number}> = ({form, defaultValue}) => {
  return (
    <Form form={form} initialValues={{parent: defaultValue}}>
      <Statistic title="# Rows Selected" value={form.getFieldValue("items")?.length} />
      <Form.Item hidden name="items"><Input /></Form.Item>
      <Form.Item required name="parent" label="Parent Collection">
        <CollectionSelect
          defaultValue={defaultValue}
          onChange={(value) => form.setFieldValue("parent", value)}
        />
      </Form.Item>
      <Form.Item required name="name" label="name">
        <Input placeholder='New collection name' />
      </Form.Item>
      <Form.Item required name="columnlist_id" label="Column List">
        <ColumnListSelect
          onChange={(value) => form.setFieldValue("columnlist_id", value)}
        />
      </Form.Item>
    </Form>
  );
};


export const CreateCollection = ({parent, selection}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  form.setFieldValue("items", selection);
  const navigate = useNavigate();

  return (
    <>
      <Typography.Text
        disabled={selection.length <= 0}
        onClick={() => setModalOpen(true)}
      >
        Create Subcollection
      </Typography.Text>
      <Modal
        open={modalOpen}
        title="Create Subcollection"
        onCancel={() => setModalOpen(false)}
        onOk={onOkFactory(form, navigate, "/collections/", setModalOpen)}
      >
        <CreateCollectionForm form={form} defaultValue={parent} />
      </Modal>
    </>
  );
};


export const AddToCollection = ({selection}) => {
  const [collections, setCollections] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    TEVService.get("/collections")
      .then(response => transformReturn(response.data))
      .then(setCollections);
  }, []);

  return (
    <>
      <Typography.Text
        disabled={selection.length <= 0}
        onClick={() => setModalOpen(true)}
      >
        Add to Collection
      </Typography.Text>
      <Modal
        open={modalOpen}
        title="Add To Collection"
        onCancel={() => setModalOpen(false)}
        onOk={onOkFactory(form, navigate, "/collections/add_items/", setModalOpen)}
      >
        <Form form={form}>
          <Statistic title="# Rows Selected" value={selection.length} />
          <Form.Item
            name="items"
            hidden
            required
            initialValue={selection}
          >
            <Input
              value={selection}
            />
          </Form.Item>
          <Form.Item
            name="collection"
            label="Collection"
            required
            rules={[
              {
                required: true,
                message: "Please select a collection.",
              }
            ]}
          >
            <TreeSelect
              treeData={collections}
              style={{ width: '100%' }}
              placeholder="Select Collection"
              showSearch
              treeNodeFilterProp='title'
            />
          </Form.Item>
          <Form.Item valuePropName="checked" name="redirect">
            <Checkbox>Redirect</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};


export const RemoveItems = ({collection_pk, selection}) => {
   const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  return (
    <>
      <Typography.Text
        disabled={selection.length < 1}
        onClick={() => setModalOpen(true)}
      >
        Remove Items
      </Typography.Text>
      <Modal
        open={modalOpen}
        title="Remove Selected Items"
        onCancel={() => setModalOpen(false)}
        onOk={
          onOkFactory(
            form,
            navigate,
            `/collections/${collection_pk}/remove_items/`,
            setModalOpen,
            "delete"
          )
        }
        okButtonProps={{
          danger: true
        }}
      >
        <Form form={form}>
          <Statistic title="# Rows Selected" value={selection.length} />
          <Form.Item
            name="items"
            hidden
            required
            initialValue={selection}
          >
            <Input value={selection} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
};


export const AssignTOIs = ({collection_pk, setModalOpen, setDisposition}) => {
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex, humanLabel) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${humanLabel}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      dataIndex: "tic_id",
      title: "TIC ID",
      sorter: (a, b) => a.tic_id - b.tic_id,
      filterSearch: (input, record) => record.tic_id == input,
      ...getColumnSearchProps('tic_id', 'TIC ID'),
    },
    {
      dataIndex: "atlas_planet_number",
      title: "Atlas Planet Identifier",
    },
    {
      dataIndex: "toi_id",
      title: "TOI ID",
      sorter: (a, b) => a.toi_id - b.toi_id
    },
    {
      dataIndex: "toi_planet_number",
      title: "TOI Planet Identifier",
    },
    {
      dataIndex: "new_toi",
      title: "New TOI Star?",
      render: (text, record, index) => (
        text ? <FontAwesomeIcon icon={["fas", "check"]} /> : null
      ),
      filters: [
        {
          text: "New TOI Star",
          value: true,
        },
        {
          text: "Existing TOI Star",
          value: false,
        },
      ],
      onFilter: (value, record) => record.new_toi === value,
    },
    {
      dataIndex: "new_toi_planet",
      title: "New TOI Planet?",
      render: (text, record, index) => (
        text ? <FontAwesomeIcon icon={["fas", "check"]} /> : null
      ),
      filters: [
        {
          text: "New TOI Planet",
          value: true,
        },
        {
          text: "Existing TOI Planet",
          value: false,
        },
      ],
      onFilter: (value, record) => record.new_toi_planet === value,
    }
  ];

  const {data, error, isLoading} = useQuery({
    queryKey: ["toi", "preview"],
    queryFn: async () => {
      const response = await TEVService.get(
        "toi-assignment/collection/",
        {params: {pk: collection_pk}}
      );
      return response.data;
    },
  });

  return (
    <>
      <Typography.Paragraph>
        The following table is a TOI assignment <b>preview</b>.
      </Typography.Paragraph>
      <Space>
        <Statistic
          title="Current TOI"
          value={isLoading ? '...' : data.current_toi}
        />
        <Divider type="vertical" />
        <Statistic
          title="Next TOI"
          value={isLoading ? '...' : data.future_toi}
        />
        <Divider type="vertical" />
        <Statistic
          title="# of New TOI Stars"
          value={isLoading ? '...' : data.new_toi_stars}
        />
        <Divider type="vertical" />
        <Statistic
          title="# of New TOI Signals"
          value={isLoading ? '...' : data.new_toi_signals}
        />
      </Space>
      {isLoading? <LoadingAnimation /> :
        <Table
          columns={columns}
          dataSource={data.assigned_tois}
          rowKey="atlas_pk"
        />
      }
      <DispositionSelect
        options={["PC", "KP", "CP"]}
        onChange={setDisposition}
        placeholder="Select TOI dispositions to set"
      />
    </>
  );
};

export const AssignTOIButton = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [disposition, setDisposition] = useState(null);
  const submitTOIs = () => {
    TEVService.post(
      "toi-assignment/collection/",
      {
        pk: props.collection_pk,
        disposition: disposition,
      }
    ).then(_ => setModalOpen(false));
  }
  return (
    <>
      <Typography.Text
        onClick={() => setModalOpen(true)}
      >
        Assign TOIs to collection
      </Typography.Text>
      <Modal
        open={modalOpen}
        title="Assign TOIs to Collection"
        onCancel={() => setModalOpen(false)}
        onOk={() => submitTOIs()}
        okButtonProps={{disabled: disposition == null}}
        width={"50%"}
      >
        <AssignTOIs {...props} setModalOpen={setModalOpen} setDisposition={setDisposition}/>
      </Modal>
    </>
  );
};
